import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { FormField, Label, Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Select } from '../ui/select-custom';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
  DialogFooter,
} from '../ui/dialog';
import { useDate } from '../../contexts/date-context';
import { formatCurrency } from '../../lib/format';
import { categoryTypes, type CategoryType, type Expense } from '../../types';

const MONTH_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const monthOptions = MONTH_LABELS.map((label, i) => ({
  value: String(i + 1),
  label,
}));

function yearOptions() {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now + 1; y >= now - 5; y--) {
    years.push({ value: String(y), label: String(y) });
  }
  return years;
}

function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

interface ParsedRow {
  id: string;
  day: number;
  title: string;
  value: number;
  categoryType: CategoryType;
}

let rowCounter = 0;

function parseRows(raw: string, defaultCategory: CategoryType): ParsedRow[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split('\t').map((p) => p.trim()).filter((p) => p !== '');
      const day = parts.length >= 2 ? parseInt(parts[0]!, 10) : 0;
      const title = parts.length >= 2 ? parts.slice(1, -1).join(' ') || parts[1]! : line;
      const valueStr = parts.length >= 2 ? parts[parts.length - 1]! : '';
      const value = parseFloat(valueStr.replace(/\./g, '').replace(',', '.'));

      rowCounter += 1;
      return {
        id: `import-${rowCounter}`,
        day: Number.isFinite(day) ? day : 0,
        title,
        value: Number.isFinite(value) ? value : 0,
        categoryType: defaultCategory,
      };
    });
}

export interface ImportExpensesDialogProps {
  open: boolean;
  onClose: () => void;
  addExpense: (
    expense: Omit<Expense, 'id' | 'createdAt' | 'userId'>
  ) => Promise<void>;
}

export function ImportExpensesDialog({
  open,
  onClose,
  addExpense,
}: ImportExpensesDialogProps) {
  const { month: currentMonth, year: currentYear } = useDate();

  const [raw, setRaw] = useState('');
  const [month, setMonth] = useState(String(currentMonth));
  const [year, setYear] = useState(String(currentYear));
  const [defaultCategory, setDefaultCategory] = useState<CategoryType>('comfort');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importError, setImportError] = useState('');

  const reset = () => {
    setRaw('');
    setRows([]);
    setProgress(0);
    setImportTotal(0);
    setImportError('');
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handleAnalyze = () => {
    setRows(parseRows(raw, defaultCategory));
  };

  const updateRow = (id: string, patch: Partial<ParsedRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const daysInMonth = getDaysInMonth(Number(month), Number(year));
  const validRows = rows.filter(
    (r) => r.title.trim() !== '' && r.value > 0 && r.day >= 1 && r.day <= daysInMonth
  );
  const invalidCount = rows.length - validRows.length;
  const totalValue = validRows.reduce((sum, r) => sum + r.value, 0);

  const handleImport = async () => {
    setImporting(true);
    setProgress(0);
    setImportError('');
    setImportTotal(validRows.length);

    const failedIds: string[] = [];
    for (const row of validRows) {
      try {
        await addExpense({
          title: row.title,
          description: '',
          value: row.value,
          date: new Date(Number(year), Number(month) - 1, row.day),
          categoryType: row.categoryType,
          tags: [],
        });
        setRows((prev) => prev.filter((r) => r.id !== row.id));
      } catch {
        failedIds.push(row.id);
      }
      setProgress((p) => p + 1);
    }

    setImporting(false);

    if (failedIds.length === 0) {
      reset();
      onClose();
    } else {
      setImportError(
        `${failedIds.length} gasto${failedIds.length === 1 ? '' : 's'} não foi importado. Ajuste ou remova a linha e tente novamente.`
      );
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop className="z-40" />
        <DialogPopup className="z-41 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar gastos em massa</DialogTitle>
            <DialogCloseButton />
          </DialogHeader>
          <DialogContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField>
                <Label>Mês</Label>
                <Select value={month} onChange={setMonth} options={monthOptions} />
              </FormField>
              <FormField>
                <Label>Ano</Label>
                <Select value={year} onChange={setYear} options={yearOptions()} />
              </FormField>
              <FormField>
                <Label>Categoria padrão</Label>
                <Select
                  value={defaultCategory}
                  onChange={(v) => setDefaultCategory(v as CategoryType)}
                  options={[
                    { value: 'needs', label: categoryTypes.needs.label },
                    { value: 'comfort', label: categoryTypes.comfort.label },
                  ]}
                />
              </FormField>
            </div>

            <FormField>
              <Label htmlFor="import-raw">
                Cole as linhas (dia, título e valor separados por tab)
              </Label>
              <textarea
                id="import-raw"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={'1\tSpotify + Meli+\t48,8\n1\tCelular. iCloud\t76,89'}
                rows={6}
                className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring font-mono"
              />
            </FormField>

            <Button type="button" variant="outline" onClick={handleAnalyze} disabled={!raw.trim()}>
              Analisar linhas
            </Button>

            {rows.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground w-16">Dia</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground">Título</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground w-28">Categoria</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-muted-foreground w-32">Valor</th>
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const isValid =
                          row.title.trim() !== '' &&
                          row.value > 0 &&
                          row.day >= 1 &&
                          row.day <= daysInMonth;
                        return (
                          <tr
                            key={row.id}
                            className={`border-t border-border ${isValid ? '' : 'bg-destructive/5'}`}
                          >
                            <td className="px-2 py-1.5">
                              <Input
                                size="sm"
                                type="number"
                                value={row.day}
                                onChange={(e) =>
                                  updateRow(row.id, { day: parseInt(e.target.value, 10) || 0 })
                                }
                                error={!isValid && (row.day < 1 || row.day > daysInMonth)}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                size="sm"
                                value={row.title}
                                onChange={(e) => updateRow(row.id, { title: e.target.value })}
                                error={!isValid && row.title.trim() === ''}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Select
                                value={row.categoryType}
                                onChange={(v) => updateRow(row.id, { categoryType: v as CategoryType })}
                                options={[
                                  { value: 'needs', label: categoryTypes.needs.label },
                                  { value: 'comfort', label: categoryTypes.comfort.label },
                                ]}
                                className="h-8 text-xs"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <CurrencyInput
                                size="sm"
                                value={row.value}
                                onChange={(v) => updateRow(row.id, { value: v })}
                                error={!isValid && row.value <= 0}
                              />
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <button
                                type="button"
                                onClick={() => removeRow(row.id)}
                                className="text-muted-foreground hover:text-destructive"
                                aria-label="Remover linha"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  <span>
                    {validRows.length} válidos
                    {invalidCount > 0 ? ` · ${invalidCount} com erro (não serão importados)` : ''}
                  </span>
                  <span className="font-semibold text-foreground">
                    Total: R$ {formatCurrency(totalValue)}
                  </span>
                </div>
              </div>
            )}

            {importError && (
              <p className="text-xs text-destructive">{importError}</p>
            )}
          </DialogContent>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={importing}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleImport}
              disabled={validRows.length === 0 || importing}
            >
              {importing
                ? `Importando ${progress}/${importTotal}...`
                : `Importar ${validRows.length} gasto${validRows.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  );
}
